import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Checkbox from "aldoo-ra/Checkbox"
import { showNotification } from "aldoo-ra/Notification"
import {
  GetDeletedSitemapEntries,
  RestoreSitemapEntries,
  DeleteSitemapEntries,
} from "../api"
import { TrashIcon } from "@heroicons/react/24/outline"

export default function RestoreDialog({
  isOpen,
  onClose,
  parentRefreshKey,
  onItemsRestored,
}) {
  const [selectedItems, setSelectedItems] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [searchText, setSearchText] = useState("")

  // Reset selections when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedItems([])
      setSearchText("")
      // Refresh the data when dialog opens
      setRefreshKey((prev) => prev + 1)
    }
  }, [isOpen])

  // Refresh when parent's refresh key changes
  useEffect(() => {
    if (isOpen && parentRefreshKey) {
      setRefreshKey((prev) => prev + 1)
    }
  }, [parentRefreshKey, isOpen])

  // Handle restore action
  const handleRestore = async () => {
    if (selectedItems.length === 0) return

    try {
      const response = await RestoreSitemapEntries(selectedItems)

      if (response.error) {
        showNotification({
          message: response.message || "Failed to restore items",
          className: "bg-admin_error text-white",
        })
      } else {
        showNotification({
          message: `${selectedItems.length} ${
            selectedItems.length === 1 ? "entry" : "entries"
          } restored successfully`,
          className: "bg-admin_primary text-white",
        })

        setSelectedItems([])
        setRefreshKey((prev) => prev + 1)

        // Notify parent component to refresh its data
        if (onItemsRestored) {
          onItemsRestored()
        }
      }
    } catch (error) {
      showNotification({
        message: "An error occurred while restoring items",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handle permanent delete action
  const handleDeleteForever = async () => {
    if (selectedItems.length === 0) return

    try {
      const response = await DeleteSitemapEntries(selectedItems, "hard")

      if (response.error) {
        showNotification({
          message: response.message || "Failed to permanently delete items",
          className: "bg-admin_error text-white",
        })
      } else {
        showNotification({
          message: `${selectedItems.length} ${
            selectedItems.length === 1 ? "entry" : "entries"
          } permanently deleted`,
          className: "bg-admin_primary text-white",
        })

        setSelectedItems([])
        setRefreshKey((prev) => prev + 1)

        // Notify parent component to refresh its data
        if (onItemsRestored) {
          onItemsRestored()
        }
      }
    } catch (error) {
      showNotification({
        message: "An error occurred while permanently deleting items",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handle select all change
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item._id))
    } else {
      setSelectedItems([])
    }
  }


  // Handle individual selection change
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemId])
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
    }
  }

  // Handle search value from the DataTable's internal search
  const handleSearchChange = (value) => {
    setSearchText(value)
  }

  // Style overrides for the DataTable
  const styleOverrides = {
    title: "absolute top-6 left-4 text-admin_text text-xl font-semibold",
    searchInput: "absolute top-4 right-12 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
    getRowStyle: (index, item) => {
      // Check if this item is selected
      if (item && selectedItems.includes(item._id)) {
        return "bg-admin_canvas" // Selected row style, matching the main component
      }
    },
  }

  // Table columns
  const columns = [
    {
      id: "select",
      headerClassName: "w-[60px]",
      header: (value, items) => (
        <Checkbox
          variant="admin"
          isOn={items.length > 0 && selectedItems.length === items.length}
          onChange={(isSelected) => handleSelectAll(items, isSelected)}
        />
      ),
      render: (value, item) => (
        <Checkbox
          variant="admin"
          isOn={selectedItems.includes(item._id)}
          onChange={(isSelected) => handleSelectItem(item._id, isSelected)}
        />
      ),
    },
    {
      id: "url",
      title: "URL",
      headerClassName: "w-1/3",
      render: (value, item) => (
        <div className="truncate max-w-md">{item.url}</div>
      ),
    },
    {
      id: "frequency",
      title: "Frequency",
      headerClassName: "w-1/5",
      render: (value, item) => item.frequency || "-",
    },
    {
      id: "priority",
      title: "Priority",
      headerClassName: "w-1/5",
      render: (value, item) =>
        item.priority !== undefined ? item.priority.toFixed(1) : "-",
    },
    {
      id: "origin",
      title: "Origin",
      headerClassName: "w-1/5",
      render: (value, item) => item.origin || "manual",
    },
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin"
      className="w-full sm:w-[500px] md:w-[85%] md:absolute md:top-[65px] h-full md:h-auto z-200"
      closeOnEscape
    >
      <div className="p-4 pt-10">
        <DataTable
          variant="admin"
          apiMethod={GetDeletedSitemapEntries}
          title="Restore Sitemap Entries"
          preferredHeight="60vh"
          itemHeight="h-[70px]"
          searchField="content.url content.origin"
          localUserPreferencesKey="admin.sitemap.restore"
        renders={columns}
          refreshKey={refreshKey}
          sort={{ deletedAt: -1 }}
          tableFooter={
            selectedItems.length > 0 && (
              <div className="flex items-center justify-end">
                <Typography 
                  variant="admin_body" 
                  className="mr-4"
                >
                  {selectedItems.length}{" "}
                  selected
                </Typography>

                <Button
                  variant="admin_primary"
                  onClick={handleRestore}
                  disabled={selectedItems.length === 0}
                  className="mr-2"
                >
                  Restore
                </Button>

                <Button
                  variant="admin_primary_red"
                  onClick={handleDeleteForever}
                  disabled={selectedItems.length === 0}
                  className="flex items-center"
                >
                  <TrashIcon className="h-5 w-5 mr-1" /> Delete Forever
                </Button>
              </div>
            )
          }
          styleOverrides={styleOverrides}
          noData={
            <Typography variant="admin_body">
              No deleted items found
            </Typography>
          }
          onSearchChange={handleSearchChange}
        />
      </div>
    </Modal>
  )
}
